import type { Building } from "building-types/building/buildingTypes";
import type { BuildingGraphFilterBuilding } from "building-types/buildingGraphFilter/buildingGraphFilterBuilding";
import type { ExecutedWallPlaneFilterMatches } from "building-types/buildingGraphFilter/wallPlaneFilterMatches";
import type { DocWallPlaneFilter } from "building-types/walls2/wallPlaneFilters";
import type { RWTable } from "common/data/table";
import { executeDocBuildingGraphFilter } from "../buildingGraphFilter/buildingGraphFilterEngine";
import { defaultWallPlaneFilters } from "../elements/walls2/wallPlaneRuleDefaults";
import memo from "../utils/memo";

function select({ buildingGraph, rooms, doc, enabledFeatures }: Building) {
  return {
    buildingGraph,
    rooms,
    wallPlaneFilters: doc.wallPlaneFilters,
    enabledFeatures,
    staticRooms: doc.rooms,
  };
}

function generate({
  buildingGraph,
  rooms,
  wallPlaneFilters,
  enabledFeatures,
  staticRooms,
}: ReturnType<typeof select>): Pick<Building, "wallPlaneFilterMatches"> {
  const wallPlaneFilterMatches: RWTable<ExecutedWallPlaneFilterMatches> = {};
  if (!enabledFeatures.wallPlanes) {
    return { wallPlaneFilterMatches };
  }

  const building: BuildingGraphFilterBuilding = buildingViewForQuery(buildingGraph, rooms, staticRooms);

  const resolvedWallPlaneFilters = { ...defaultWallPlaneFilters, ...wallPlaneFilters };

  for (const filter of Object.values(resolvedWallPlaneFilters)) {
    addFilterExecutionResult(wallPlaneFilterMatches, filter, building);
  }

  return { wallPlaneFilterMatches };
}

/**
 * Builds the query view required by the filter engine. ROOM and ROOM_EDGE sources
 * iterate top-level generated rooms; the selective doc carries only the room table the
 * engine falls back to for rooms not present in generated `rooms`.
 */
function buildingViewForQuery(
  buildingGraph: Building["buildingGraph"],
  rooms: Building["rooms"],
  staticRooms: Building["doc"]["rooms"],
): BuildingGraphFilterBuilding {
  return {
    buildingGraph,
    rooms,
    doc: { rooms: staticRooms },
  };
}

function addFilterExecutionResult(
  wallPlaneFilterMatches: RWTable<ExecutedWallPlaneFilterMatches>,
  filter: DocWallPlaneFilter,
  building: BuildingGraphFilterBuilding,
): void {
  const targetType = "FACE_NODE";
  const matches = executeDocBuildingGraphFilter(
    "FACE_NODE",
    filter.filterGraphs,
    filter.filterGraphId,
    building,
    filter.id,
  );
  wallPlaneFilterMatches[filter.id] = {
    targetType,
    matches,
  };
}

export default memo(select, generate);
